import { v4 as createUUID } from "uuid";

import coinflip from "@atlassian/wac-coinflip";
import {
    getCapabilityRate,
    getAwaitBM3TTIList
} from "@atlassian/wac-ufo-config";
import { clearActiveTrace } from "@atlassian/wac-ufo-experience-trace-id-context";
import type { TraceIdContext } from "@atlassian/wac-ufo-experience-trace-id-context/src/types";
import {
    currentFeatureFlagsAccessed,
    allFeatureFlagsAccessed
} from "@atlassian/wac-ufo-feature-flags-accessed";
import type {
    LabelStack,
    SegmentLabel
} from "@atlassian/wac-ufo-interaction-context";
import { getInteractionId } from "@atlassian/wac-ufo-interaction-id-context";

import interactions from "./common/constants";

import type {
    MarkType,
    SpanType,
    Span,
    Mark,
    InteractionType,
    AbortReasonType,
    InteractionMetrics,
    ReactProfilerTiming,
    RequestInfo,
    ApdexType,
    LifecycleMarkType,
    SegmentInfo,
    CustomData,
    CustomTiming
} from "./common/types";
import type { EventInfo } from "react-loosely-lazy";

export type {
    InteractionMetrics,
    LifecycleMarkType,
    Span,
    Mark,
    MarkType,
    InteractionType,
    AbortReasonType,
    ReactProfilerTiming,
    RequestInfo,
    ApdexType,
    CustomData,
    CustomTiming
};

const PreviousInteractionLog = {
    name: undefined as string | undefined
};

const interactionQueue: { id: string; data: InteractionMetrics }[] = [];
const segmentCache = new Map<string, SegmentInfo>();
const CLEANUP_TIMEOUT = 60 * 1000;
const CLEANUP_TIMEOUT_AFTER_APDEX = 15 * 1000;

type SegmentObserver = {
    onAdd: (segment: SegmentInfo) => void;
    onRemove: (segment: SegmentInfo) => void;
};
const segmentObservers: SegmentObserver[] = [];

function addSegmentObserver(observer: SegmentObserver) {
    segmentObservers.push(observer);

    for (const segmentInfo of segmentCache.values()) {
        observer.onAdd(segmentInfo);
    }
}
function removeSegmentObserver(observer: SegmentObserver) {
    const index = segmentObservers.findIndex((obs) => obs === observer);

    if (index !== -1) {
        segmentObservers.splice(index, 1);
    }
}

export function remove(interactionId: string) {
    interactions.delete(interactionId);
}

export function addMetadata(
    interactionId: string,
    data: { [key: string]: unknown }
) {
    const interaction = interactions.get(interactionId);
    if (interaction != null) {
        Object.keys(data).forEach((key) => {
            interaction.metaData[key] = data[key];
        });
    }
}

export function addCustomData(
    interactionId: string,
    labelStack: LabelStack,
    data: CustomData
) {
    const interaction = interactions.get(interactionId);
    if (interaction != null) {
        Object.keys(data).forEach((i) => {
            interaction.customData.push({ labelStack, data: { [i]: data[i] } });
        });
    }
}

export function addCustomTiming(
    interactionId: string,
    labelStack: LabelStack,
    data: CustomTiming
) {
    const interaction = interactions.get(interactionId);
    if (interaction != null) {
        interaction.customTimings.push({ labelStack, data });
    }
}

export function addMark(
    interactionId: string,
    type: MarkType,
    name: string,
    labelStack: LabelStack | null,
    time: number = performance.now()
) {
    const interaction = interactions.get(interactionId);
    if (interaction != null) {
        interaction.marks.push({ type, name, labelStack, time });
    }
}

export function addMarkToAll(
    type: MarkType,
    name: string,
    labelStack: LabelStack | null,
    time: number = performance.now()
) {
    interactions.forEach((interaction) => {
        interaction.marks.push({ type, name, labelStack, time });
    });
}

export function addSpan(
    interactionId: string,
    type: SpanType,
    name: string,
    labelStack: LabelStack | null,
    start: number,
    end: number = performance.now(),
    size?: number
) {
    const interaction = interactions.get(interactionId);
    if (interaction != null) {
        interaction.spans.push({ type, name, labelStack, start, end, size });
    }
}

export function addSpanToAll(
    type: SpanType,
    name: string,
    labelStack: LabelStack | null,
    start: number,
    end: number = performance.now(),
    size = 0
) {
    interactions.forEach((interaction) => {
        interaction.spans.push({ type, name, labelStack, start, end, size });
    });
}

export function addPreload(moduleId: string, timestamp: number) {
    addMarkToAll("bundle_preload", moduleId, null, timestamp);
}
export function addLoad(identifier: string, start: number, end: number) {
    addSpanToAll("bundle_load", identifier, null, start, end - start);
}

const moduleLoadingRequests: {
    [key: string]: {
        start: number;
        timeoutId: ReturnType<typeof setTimeout> | number | undefined;
    };
} = {};

export function extractModuleName(input: string): string {
    let result = input ?? "";

    result = result.replace(/^\.\/src\/packages\//, "");
    result = result.replace(/^\.\/node_modules\//, "");
    result = result.replace(/(\/src)?\/(index|main)\.(tsx|ts|js|jsx)$/, "");

    return result;
}

export function addHold(
    interactionId: string,
    labelStack: LabelStack,
    name: string
) {
    const interaction = interactions.get(interactionId);
    const id = createUUID();
    if (interaction != null) {
        const start = performance.now();
        interaction.holdActive.set(id, { labelStack, name, start });
        return () => {
            const end = performance.now();
            const currentInteraction = interactions.get(interactionId);
            const currentHold = interaction.holdActive.get(id);
            if (currentInteraction != null && currentHold != null) {
                currentInteraction.holdInfo.push({ ...currentHold, end });
                interaction.holdActive.delete(id);
            }
        };
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {};
}

export function addHoldByID(
    interactionId: string,
    labelStack: LabelStack,
    name: string,
    id: string,
    ignoreOnSubmit?: boolean
) {
    const interaction = interactions.get(interactionId);
    if (interaction != null) {
        const start = performance.now();
        interaction.holdActive.set(id, {
            labelStack,
            name,
            start,
            ignoreOnSubmit
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {};
}

export function removeHoldByID(interactionId: string, id: string) {
    const interaction = interactions.get(interactionId);

    if (interaction != null) {
        const end = performance.now();
        const currentInteraction = interactions.get(interactionId);
        const currentHold = interaction.holdActive.get(id);
        if (currentInteraction != null && currentHold != null) {
            currentInteraction.holdInfo.push({ ...currentHold, end });
            interaction.holdActive.delete(id);
        }
    }
}

export function getCurrentInteractionType(interactionId: string) {
    const interaction = interactions.get(interactionId);
    if (interaction) {
        return interaction.type;
    }
    return null;
}

export const ModuleLoadingProfiler = {
    onPreload(moduleId: string, _priority?: number) {
        addPreload(extractModuleName(moduleId), performance.now());
    },
    onLoadStart(info: EventInfo): void {
        const timeoutId = setTimeout(() => {
            delete moduleLoadingRequests[info.identifier];
        }, 30000);
        const request = {
            start: performance.now(),
            timeoutId
        };
        moduleLoadingRequests[info.identifier] = request;
    },
    onLoadComplete(info: EventInfo): void {
        const request = moduleLoadingRequests[info.identifier];
        if (request) {
            clearTimeout(request.timeoutId);
            delete moduleLoadingRequests[info.identifier];
            addLoad(
                extractModuleName(info.identifier),
                request.start,
                performance.now()
            );
        }
    },
    placeholderFallBackMounted(id: string, moduleId: string): void {
        const interactionId = getInteractionId();
        const currentInteractionId = interactionId.current;
        if (currentInteractionId) {
            addHoldByID(currentInteractionId, [], moduleId, id);
        }
    },
    placeholderFallBackUnmounted(id: string): void {
        const interactionId = getInteractionId();
        const currentInteractionId = interactionId.current;
        if (currentInteractionId) {
            removeHoldByID(currentInteractionId, id);
        }
    }
};

export function addError(
    interactionId: string,
    name: string,
    labelStack: LabelStack | null,
    errorType: string,
    errorMessage: string,
    errorStack?: string
) {
    const interaction = interactions.get(interactionId);
    if (interaction != null) {
        interaction.errors.push({
            name,
            labelStack,
            errorType,
            errorMessage,
            errorStack
        });
    }
}

export function addErrorToAll(
    name: string,
    labelStack: LabelStack | null,
    errorType: string,
    errorMessage: string,
    errorStack?: string
) {
    interactions.forEach((interaction) => {
        interaction.errors.push({
            name,
            labelStack,
            errorType,
            errorMessage,
            errorStack
        });
    });
}

export const addProfilerTimings = (
    interactionId: string,
    labelStack: LabelStack,
    type: "mount" | "update",
    actualDuration: number,
    baseDuration: number,
    startTime: number,
    commitTime: number
) => {
    const interaction = interactions.get(interactionId);
    if (interaction != null) {
        interaction.reactProfilerTimings.push({
            labelStack,
            type,
            actualDuration,
            baseDuration,
            startTime,
            commitTime
        });
    }
};

const pushToQueue = (id: string, data: InteractionMetrics) => {
    interactionQueue.push({ id, data });
};

let handleInteraction = pushToQueue;

function callCleanUpCallbacks(interaction: InteractionMetrics) {
    interaction.cleanupCallbacks.reverse().forEach((cleanUpCallback) => {
        cleanUpCallback();
    });
}

const finishInteraction = (
    id: string,
    data: InteractionMetrics,
    endTime: number = performance.now()
) => {
    // eslint-disable-next-line no-param-reassign
    data.end = endTime;
    if (data.featureFlags) {
        // eslint-disable-next-line no-param-reassign
        data.featureFlags.during = Object.fromEntries(
            currentFeatureFlagsAccessed
        );
    }
    clearActiveTrace();
    callCleanUpCallbacks(data);
    remove(id);
    PreviousInteractionLog.name = data.ufoName;
    handleInteraction(id, data);
};

export const sinkInteractionHandler = (
    sinkFn: (id: string, data: InteractionMetrics) => void
) => {
    if (handleInteraction === pushToQueue) {
        handleInteraction = sinkFn;
        interactionQueue.forEach((interaction) => {
            sinkFn(interaction.id, interaction.data);
        });
        interactionQueue.length = 0;
    }
};

export function tryComplete(interactionId: string, endTime?: number) {
    const interaction = interactions.get(interactionId);
    if (interaction != null) {
        const noMoreHolds = interaction.holdActive.size === 0;
        if (noMoreHolds) {
            finishInteraction(interactionId, interaction, endTime);
        }
    }
}

function callCancelCallbacks(interaction: InteractionMetrics) {
    interaction.cancelCallbacks.reverse().forEach((cancelCallback) => {
        cancelCallback();
    });
}

export function abort(interactionId: string, abortReason: AbortReasonType) {
    const interaction = interactions.get(interactionId);
    if (interaction != null) {
        callCancelCallbacks(interaction);
        interaction.abortReason = abortReason;
        finishInteraction(interactionId, interaction);
    }
}

export function abortByNewInteraction(
    interactionId: string,
    interactionName: string
) {
    const interaction = interactions.get(interactionId);
    if (interaction != null) {
        callCancelCallbacks(interaction);
        interaction.abortReason = "new_interaction";
        interaction.abortedByInteractionName = interactionName;
        finishInteraction(interactionId, interaction);
    }
}

export function abortAll(
    abortReason: AbortReasonType,
    abortedByInteractionName?: string
) {
    interactions.forEach((interaction, interactionId) => {
        callCancelCallbacks(interaction);
        // eslint-disable-next-line no-param-reassign
        interaction.abortReason = abortReason;
        if (abortedByInteractionName != null) {
            // eslint-disable-next-line no-param-reassign
            interaction.abortedByInteractionName = abortedByInteractionName;
        }
        finishInteraction(interactionId, interaction);
    });
}

export function addOnCancelCallback(id: string, cancelCallback: () => void) {
    const interaction = interactions.get(id);

    interaction?.cancelCallbacks.push(cancelCallback);
}

export function addNewInteraction(
    interactionId: string,
    ufoName: string,
    type: InteractionType,
    startTime: number,
    rate: number,
    labelStack: LabelStack | null,
    routeName?: string | null,
    trace: TraceIdContext | null = null
) {
    let previousTime = startTime;
    let timeoutTime = CLEANUP_TIMEOUT;
    const timerID: ReturnType<typeof setTimeout> | undefined = setTimeout(
        () => {
            abort(interactionId, "timeout");
        },
        CLEANUP_TIMEOUT
    );

    function changeTimeout(this: InteractionMetrics, newTime: number) {
        // we compare if the time left is lower than the new time to no
        // extend the timeout beyond the initial waiting time
        const currentTime = performance.now();
        const timeLeft = timeoutTime - (currentTime - previousTime);
        if (timeLeft < newTime) {
            return;
        }
        clearTimeout(this.timerID);
        const newTimerID: ReturnType<typeof setTimeout> | undefined =
            setTimeout(() => {
                abort(interactionId, "timeout");
            }, newTime);
        timeoutTime = newTime;
        previousTime = currentTime;
        this.timerID = newTimerID;
    }

    const addFeatureFlagsToInteraction = coinflip(
        getCapabilityRate("feature_flag_access")
    );

    const metrics: InteractionMetrics = {
        start: startTime,
        end: 0,
        ufoName,
        type,
        previousInteractionName: PreviousInteractionLog.name,
        marks: [],
        customData: [],
        customTimings: [],
        spans: [],
        requestInfo: [],
        reactProfilerTimings: [],
        holdInfo: [],
        holdActive: new Map(),
        // measure when we execute this code
        // from this we can measure the input delay -
        // how long the browser took to hand execution back to JS)
        measureStart: performance.now(),
        rate,
        cancelCallbacks: [],
        metaData: {},
        errors: [],
        apdex: [],
        labelStack,
        routeName: routeName ?? ufoName,
        featureFlags: addFeatureFlagsToInteraction
            ? {
                  prior: Object.fromEntries(allFeatureFlagsAccessed),
                  during: {}
              }
            : undefined,
        knownSegments: [],
        cleanupCallbacks: [],
        awaitReactProfilerCount: 0,
        redirects: [],
        timerID,
        changeTimeout,
        trace
    };
    if (addFeatureFlagsToInteraction) {
        currentFeatureFlagsAccessed.clear();
    }
    interactions.set(interactionId, metrics);

    const segmentObserver: SegmentObserver = {
        onAdd(segment) {
            metrics.knownSegments.push(segment);
        },
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onRemove() {}
    };
    addSegmentObserver(segmentObserver);
    metrics.cleanupCallbacks.push(() => {
        removeSegmentObserver(segmentObserver);
    });
    metrics.cleanupCallbacks.push(() => {
        clearTimeout(metrics.timerID);
    });
    const awaitBM3TTIList = getAwaitBM3TTIList();
    if (awaitBM3TTIList.includes(ufoName)) {
        addHoldByID(interactionId, [], ufoName, ufoName, true);
    }
}

export function addApdexToAll(apdex: ApdexType) {
    interactions.forEach((interaction, key) => {
        interaction.apdex.push(apdex);
        interaction.changeTimeout(CLEANUP_TIMEOUT_AFTER_APDEX);
        removeHoldByID(key, interaction.ufoName);
    });
}

export function addApdex(
    interactionId: string,
    apdexInfo: { key: string; stopTime: number; labelStack?: LabelStack }
) {
    const interaction = interactions.get(interactionId);
    if (interaction != null) {
        interaction.apdex.push(apdexInfo);
        interaction.changeTimeout(CLEANUP_TIMEOUT_AFTER_APDEX);
        removeHoldByID(interactionId, interaction.ufoName);
    }
}

export function addRequestInfo(
    interactionId: string,
    labelStack: LabelStack,
    requestInfo: RequestInfo
) {
    const interaction = interactions.get(interactionId);
    if (interaction != null) {
        interaction.requestInfo.push({
            labelStack,
            ...requestInfo
        });
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isSegmentLabel(obj: any): obj is SegmentLabel {
    return (
        obj && typeof obj.name === "string" && typeof obj.segmentId === "string"
    );
}

function getSegmentCacheKey(labelStack: LabelStack) {
    return labelStack
        .map((l) => {
            if (isSegmentLabel(l)) {
                return `${l.name}_${l.segmentId}`;
            }
            return l.name;
        })
        .join("|");
}

export function addSegment(labelStack: LabelStack) {
    const key = getSegmentCacheKey(labelStack);
    const existingSegment = segmentCache.get(key);
    if (!existingSegment) {
        const segmentInfo: SegmentInfo = { labelStack };
        segmentCache.set(key, segmentInfo);
        segmentObservers.forEach((observer) => {
            observer.onAdd(segmentInfo);
        });
    }
}

export function removeSegment(labelStack: LabelStack) {
    const key = getSegmentCacheKey(labelStack);
    const segmentInfo = segmentCache.get(key);

    if (segmentInfo) {
        segmentCache.delete(JSON.stringify(labelStack));

        segmentObservers.forEach((observer) => {
            observer.onRemove(segmentInfo);
        });
    }
}

export function addRedirect(
    interactionId: string,
    fromUfoName: string,
    nextUfoName: string,
    nextRouteName: string,
    time: number
) {
    const interaction = interactions.get(interactionId);
    if (interaction != null) {
        interaction.ufoName = nextUfoName;
        interaction.routeName = nextRouteName;
        interaction.redirects.push({ fromInteractionName: fromUfoName, time });
    }
}
